/**
 * =============================================================================
 *  HINH HOC CUA MOT HOP CHU - MOT PHEP TINH DUY NHAT
 * =============================================================================
 *
 *  Dung cho ca ghi chu dang hop (callout) LAN hop chu tu do. Ket qua cua ham
 *  nay di vao DUNG MOT cho: `Annotation.rect`. Ban xem truoc tren man hinh ve
 *  theo `rect`, va duong ghi dung dong hinh dang theo `rect`. Khong ben nao tu
 *  do lai kich thuoc cua rieng minh.
 *
 *  Do la bai hoc da tra gia hai vong o duong sua chu: khi ban xem truoc va
 *  duong ghi tinh hinh hoc bang hai doan ma khac nhau, chung troi khoi nhau va
 *  ban xem truoc noi doi voi nguoi dung. Xem core/edit/coverRect.ts.
 *
 * =============================================================================
 *  VI SAO CHIEU CAO KHONG DUOC TINH TU CO CHU
 * =============================================================================
 *  Cam do tu phep do that tren chinh ban Noto Sans dang dong goi (don vi em,
 *  tinh tu duong co so):
 *
 *      'x'  vuon 0.536       'g'  thut -0.240
 *      'Ệ'  vuon 0.944       'Ề'  vuon 0.977
 *
 *  Gan gap doi giua chu thuong ASCII va mot nguyen am chong hai tang dau. Mot
 *  hop cao "1.2 x co chu" om vua khit chu tieng Anh se CAT CUT dau cua "Ề",
 *  "Ỗ", "Ữ". Tren man hinh trinh duyet co the van thay (trinh duyet cho chu
 *  tran ra ngoai the), nhung /BBox cua dong hinh dang PDF thi CAT THAT - va
 *  nguoi dung chi phat hien ra sau khi da xuat tep.
 *
 *  Nen chieu cao o day = MAX cua hai phep do, khong phai mot cong thuc:
 *    - chi so doc cua CA FONT (ascent/descent trong bang hhea/OS/2), lam SAN.
 *      No phu tron ven bang chu cai tieng Viet, va no la mot hang so - nho vay
 *      hop khong nhay len xuong khi nguoi dung go them mot dau nang.
 *    - hop bao glyph THAT (glyph.bbox) cua dung nhung ky tu co trong chuoi, do
 *      qua fontkit. No la thu bat duoc truong hop mot ky tu vuot ra ngoai chi
 *      so cua font - co that, va khong cong thuc nao doan truoc duoc.
 *
 * =============================================================================
 *  VI SAO GHI CHU MAC DINH LA HOP CHU DOC DUOC
 * =============================================================================
 *  Chu thich /Text theo dac ta la mot BIEU TUONG nho: noi dung chi hien khi ro
 *  chuot hoac mo bang chu thich. Do la PDF dung chuan, va Acrobat lam dung nhu
 *  vay. Nhung nguoi dung go xong mot doan ghi chu roi chi thay "hình vuông nhỏ
 *  màu vàng" thi ho khong coi do la mot ghi chu - ho coi do la mot loi.
 *
 *  Mot ghi chu khong ai doc duoc la mot ghi chu khong ai dung. Nen MAC DINH la
 *  'callout': mot hop giay vang co chu ben trong, doc duoc ngay tren trang, ke
 *  ca khi in ra. Ai muon hanh vi cua Acrobat thi doi sang 'icon' - lua chon do
 *  van con, chi khong con la mac dinh.
 */
import type { PdfRect, TextAlign } from '@pdfeditor/types';
import type { Font } from '../core/fonts/fontkit';
import { measureText, wrapText, lineOriginX, type TextState } from '../core/edit/layout';
import { checkCoverage, type CoverageResult } from '../core/fonts/coverage';
import {
  peekBundledFamilyFace,
  normalizeFontFamilyId,
  type TextFaceStyle,
} from '../core/fonts/bundled';

export type { TextFaceStyle };

/**
 * =============================================================================
 *  MOT NGUON MAT CHU, KHONG PHAI MOT FONT
 * =============================================================================
 *
 *  Dam va nghieng la MAT CHU THAT (NotoSans-Bold.ttf, -Italic.ttf, -BoldItalic
 *  .ttf), khong phai hieu ung ve len ban Regular. Ma nghia la: ban Bold RONG
 *  HON ban Regular o cung co chu, va ban Italic co khoang hai ben khac han.
 *
 *  Do chu dam bang so do cua Regular cho ra mot ket qua ngat dong SAI - sai tren
 *  man hinh va sai trong tep, theo hai kieu khac nhau. Nen phep do phai duoc
 *  trao MOT NGUON MAT CHU chu khong phai mot font: no tu lay dung mat chu ung
 *  voi `bold` / `italic` cua chinh doi tuong dang do.
 *
 *  Van nhan mot `Font` tran: duong ghi (core/annotations/write.ts) da giai xong
 *  mat chu TRUOC khi do, vi no con phai NHUNG dung mat chu do vao tep. Truyen
 *  thang mat chu da giai la dung; truyen ban Regular kem mot doi tuong dam thi
 *  khong - va do la ly do tham so nay duoc dat ten la "nguon" chu khong phai
 *  "font".
 */
export interface FontFaces {
  /** Nhan dang, de phan biet voi mot `Font` tran ma khong can instanceof. */
  readonly faces: true;
  /** Mat chu ung voi kieu duoc yeu cau. Khong bao gio tra ve null. */
  face(style: TextFaceStyle): Font;
}

export type FontSource = Font | FontFaces;

/** Rut MOT mat chu ra khoi nguon. Mot `Font` tran la "da giai roi, dung luon". */
export function faceOf(source: FontSource, style: TextFaceStyle): Font {
  return (source as Partial<FontFaces>).faces === true
    ? (source as FontFaces).face(style)
    : (source as Font);
}

/**
 * Nguon mat chu lay tu ho font dong goi.
 *
 * Dong bo co chu dich: lop hien thi ve trong mot lan render va khong cho duoc.
 * Mat chu chua nap thi peekBundledFamilyFace() lui ve Regular VA kich hoat nap;
 * khi tep font ve toi noi, onBundledFontLoaded() bao cho lop hien thi ve lai
 * bang so do that. Nguoi dung thay chu dam tre mot nhip, khong thay chu sai mai.
 *
 * `fallback` bat buoc: ben goi phai co san MOT mat chu chac chan (ban Regular da
 * nap) truoc khi do bat cu thu gi. Khong co no thi ham nay se phai tra ve null
 * va moi ben goi lai phai doan xem "khong co font" nghia la gi.
 *
 * `familyId` chon HO FONT: hop chu mang `fontFamily` nao thi do bang ho do. Bo
 * qua = ho mac dinh (noto-sans), giu nguyen hanh vi cu. Ho khac Regular la mot
 * MAT CHU KHAC (Noto Serif rong hon Noto Sans o cung co chu), nen do bang ho sai
 * cho ra ket qua ngat dong sai - do la bai hoc so 4, chi la giua cac HO chu
 * khong phai giua cac mat chu.
 */
export function bundledFaces(fallback: Font, familyId?: string): FontFaces {
  const family = normalizeFontFamilyId(familyId);
  return {
    faces: true,
    face: (style: TextFaceStyle) => peekBundledFamilyFace(family, style)?.font ?? fallback,
  };
}

/** Le trong hop chu, moi phia, tinh bang diem. */
export const TEXT_BOX_PADDING_PT = 4;

/** Buoc dong theo boi so co chu. 1.32 la khoang cach doc thoai mai cho chu Viet. */
export const LINE_HEIGHT_RATIO = 1.32;

/** Co chu mac dinh cua mot hop chu moi. */
export const DEFAULT_TEXT_SIZE_PT = 12;

/** Be rong mac dinh cua mot hop chu moi, tinh bang diem. */
export const DEFAULT_TEXT_BOX_WIDTH_PT = 220;

/** Mau giay vang co dien cua ghi chu dinh. */
export const NOTE_FILL_RGB: readonly [number, number, number] = [1, 0.898, 0.443];

/** Mau chu mac dinh: den mem, khong phai den tuyet doi. */
export const DEFAULT_TEXT_COLOR_RGB: readonly [number, number, number] = [0.09, 0.09, 0.11];

export interface TextBoxStyle {
  fontSizePt: number;
  align: TextAlign;
  /** Be rong hop (ke ca le trong). Chieu cao thi do ra, khong nhan vao. */
  widthPt: number;
  /** Mat chu DAM. Chon mot tep font khac, khong to day net cua ban Regular. */
  bold?: boolean | undefined;
  /** Mat chu NGHIENG. Cung vay: mot tep font khac, khong phai phep truot hinh. */
  italic?: boolean | undefined;
}

/** Mot dong da dat xong trong hop, toa do TUONG DOI so voi goc duoi-trai cua hop. */
export interface PlacedBoxLine {
  text: string;
  widthPt: number;
  /** Do lech tu mep TRAI cua hop toi diem bat dau cua dong. */
  xPt: number;
  /** Do cao cua DUONG CO SO dong nay so voi day hop. */
  baselineYPt: number;
}

export interface TextBoxMetrics {
  /**
   * Mat chu THAT SU da duoc dung de do va de kiem do phu glyph.
   *
   * Lo ra ngoai de ben goi doi chieu duoc: neu duong ghi nhung mot mat chu khac
   * voi mat chu da do o day thi ngat dong trong tep se lech khoi ban xem truoc.
   */
  face: Font;
  lines: string[];
  placedLines: PlacedBoxLine[];
  widthPt: number;
  /** Chieu cao do tu muc that cua glyph, da cong le trong ca hai phia. */
  heightPt: number;
  lineHeightPt: number;
  /** Muc vuon cao nhat tren duong co so trong CA hop, do tu glyph.bbox. */
  ascentPt: number;
  /** Muc thut sau nhat duoi duong co so (so duong), do tu glyph.bbox. */
  descentPt: number;
  /** Do phu glyph cua chuoi. `ok === false` nghia la se co o vuong trong. */
  coverage: CoverageResult;
}

interface GlyphBox {
  minY: number;
  maxY: number;
}

/**
 * Hop bao MUC that cua mot glyph, don vi em.
 *
 * Tra ve null khi font khong tra loi duoc - ben goi lui ve chi so ascent/descent
 * cua ca font, thu luon luon co va luon >= muc that.
 */
function glyphInkEm(font: Font, codePoint: number): GlyphBox | null {
  try {
    const glyph = font.glyphForCodePoint(codePoint) as unknown as { bbox?: GlyphBox } | null;
    const bbox = glyph?.bbox;
    if (!bbox || !Number.isFinite(bbox.minY) || !Number.isFinite(bbox.maxY)) return null;
    const upem = font.unitsPerEm || 1000;
    return { minY: bbox.minY / upem, maxY: bbox.maxY / upem };
  } catch {
    return null;
  }
}

/** Chi so ascent / descent cua ca font, don vi em. Luoi an toan cua phep do glyph. */
function fontInkEm(font: Font): GlyphBox {
  const upem = font.unitsPerEm || 1000;
  const f = font as unknown as { ascent?: number; descent?: number };
  const ascent = Number.isFinite(f.ascent) ? (f.ascent as number) / upem : 1;
  // descent cua font la SO AM. Ta giu nguyen dau o day va doi dau o cho dung.
  const descent = Number.isFinite(f.descent) ? (f.descent as number) / upem : -0.25;
  return { minY: descent, maxY: ascent };
}

/**
 * Muc that cua mot chuoi, don vi em.
 *
 * DO TREN CA HOP chu khong tung dong: dong dau va dong cuoi phai chua duoc ky tu
 * cao nhat / thap nhat co the xuat hien khi nguoi dung go tiep. Do rieng tung
 * dong se cho ra mot hop "nhay len nhay xuong" moi lan them mot dau nang.
 */
export function inkExtentEm(font: Font, text: string): GlyphBox {
  // SAN: chi so doc cua ca font. Luon phu bang chu cai tieng Viet, va la mot
  // hang so - nho vay hop khong nhay len xuong khi go them mot dau nang vao mot
  // dong dang toan ky tu thap.
  const floor = fontInkEm(font);
  let maxY = floor.maxY;
  let minY = floor.minY;

  // TRAN: mot ky tu co the vuot ra NGOAI chi so cua font. Khi do phep do glyph
  // that thang, va do la ly do vong lap nay ton tai - khong phai de lam dep.
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp === 0x0a || cp === 0x0d || cp === 0x09 || cp === 0x20) continue;
    const ink = glyphInkEm(font, cp);
    if (!ink) continue;
    if (ink.maxY > maxY) maxY = ink.maxY;
    if (ink.minY < minY) minY = ink.minY;
  }

  return { minY, maxY };
}

function stateFor(fontSizePt: number): TextState {
  return { fontSizePt, charSpacing: 0, wordSpacing: 0, horizontalScale: 100 };
}

/**
 * Do mot hop chu: ngat dong theo be rong cho truoc, roi TINH CHIEU CAO TU MUC
 * THAT cua glyph.
 *
 * `widthPt` la be rong NGOAI cua hop; vung dat chu hep hon 2 lan le trong.
 */
export function measureTextBox(
  source: FontSource,
  text: string,
  style: TextBoxStyle,
): TextBoxMetrics {
  // MAT CHU TRUOC MOI PHEP DO. Ban Bold rong hon ban Regular o cung co chu, nen
  // do bang Regular roi ghi ra bang Bold cho ra mot hop chat hon chinh chu ben
  // trong no - va nguoi dung chi thay dieu do sau khi xuat tep.
  const font = faceOf(source, style);
  const fontSizePt = Math.max(1, style.fontSizePt);
  const innerWidth = Math.max(4, style.widthPt - TEXT_BOX_PADDING_PT * 2);
  const state = stateFor(fontSizePt);

  // Chuoi rong van phai cho ra mot hop CO KICH THUOC: mot hop chu vua tao chua
  // co chu nao van phai nhin thay va bam trung duoc, neu khong thi nguoi dung
  // bam tao roi khong thay gi va tuong la khong an.
  const body = text.length > 0 ? text : ' ';
  const lines = wrapText(font, body, innerWidth, state);

  const ink = inkExtentEm(font, body);
  const ascentPt = ink.maxY * fontSizePt;
  const descentPt = -ink.minY * fontSizePt;
  const lineHeightPt = fontSizePt * LINE_HEIGHT_RATIO;

  // Dong dau chiem ascent; moi dong sau day them mot buoc dong; dong cuoi con
  // thut xuong descent. Do la chieu cao THAT cua khoi muc, khong phai mot cong
  // thuc theo co chu.
  const textHeight = ascentPt + (lines.length - 1) * lineHeightPt + descentPt;
  const heightPt = textHeight + TEXT_BOX_PADDING_PT * 2;

  const placedLines: PlacedBoxLine[] = lines.map((line, index) => {
    const widthPt = measureText(font, line, state);
    return {
      text: line,
      widthPt,
      xPt: lineOriginX(TEXT_BOX_PADDING_PT, innerWidth, widthPt, style.align),
      // Duong co so dem TU DAY HOP len, vi he toa do PDF huong y len tren.
      baselineYPt:
        TEXT_BOX_PADDING_PT + descentPt + (lines.length - 1 - index) * lineHeightPt,
    };
  });

  return {
    face: font,
    lines,
    placedLines,
    widthPt: style.widthPt,
    heightPt,
    lineHeightPt,
    ascentPt,
    descentPt,
    coverage: checkCoverage(font, body),
  };
}

/**
 * Hop chu nhat cuoi cung cua mot hop chu, neo o goc TREN-TRAI.
 *
 * Neo tren-trai chu khong phai duoi-trai la co y: nguoi dung bam mot diem roi
 * go, va chu phai moc XUONG tu diem do nhu moi trinh soan thao khac. Neo duoi
 * thi hop se boc len tren va che mat dong ma ho vua bam vao.
 */
export function textBoxRect(
  anchorTopLeft: { x: number; y: number },
  metrics: TextBoxMetrics,
): PdfRect {
  return {
    x: anchorTopLeft.x,
    y: anchorTopLeft.y - metrics.heightPt,
    width: metrics.widthPt,
    height: metrics.heightPt,
  };
}

/**
 * Do lai hop cua mot doi tuong chu sau khi noi dung hoac kieu chu doi.
 *
 * DIEM NEO LA GOC TREN-TRAI va no khong doi. Neo goc duoi thi go them mot dong
 * se day ca hop len tren - chu da go bi truot khoi cho nguoi dung dat no.
 *
 * Ham nay la duong DUY NHAT sinh ra `rect` cho 'free-text' va cho ghi chu dang
 * hop: moi cho doi noi dung, doi co chu, doi can le hay doi be rong deu goi no.
 * Mot cho tu tinh rieng la mot cho se troi khoi phan con lai.
 */
export function reflowTextAnnotation(
  source: FontSource,
  current: {
    rect: PdfRect;
    contents?: string;
    fontSizePt?: number;
    textAlign?: TextAlign;
    bold?: boolean | undefined;
    italic?: boolean | undefined;
  },
  patch: {
    contents?: string;
    fontSizePt?: number;
    textAlign?: TextAlign;
    widthPt?: number;
    bold?: boolean | undefined;
    italic?: boolean | undefined;
  } = {},
): { rect: PdfRect; metrics: TextBoxMetrics } {
  const anchor = { x: current.rect.x, y: current.rect.y + current.rect.height };
  const metrics = measureTextBox(source, patch.contents ?? current.contents ?? '', {
    fontSizePt: patch.fontSizePt ?? current.fontSizePt ?? DEFAULT_TEXT_SIZE_PT,
    align: patch.textAlign ?? current.textAlign ?? 'left',
    widthPt: patch.widthPt ?? current.rect.width ?? DEFAULT_TEXT_BOX_WIDTH_PT,
    // BAT DAM / NGHIENG CUNG LA MOT PHEP DO LAI. Ban Bold rong hon, nen mot hop
    // vua khit o ban Regular se tran ra mot dong nua khi bat dam; khong do lai
    // thi dong tran do nam ngoai hop, tren man hinh lan trong tep.
    bold: patch.bold ?? current.bold,
    italic: patch.italic ?? current.italic,
  });
  return { rect: textBoxRect(anchor, metrics), metrics };
}

/**
 * BO CUC CUOI CUNG cua mot doi tuong chu. MOT phep tinh, HAI ben goi.
 *
 * Ben goi la duong ghi (core/annotations/write.ts, de dung /AP) va lop hien thi
 * (viewer/AnnotationLayer.tsx, de ve len man hinh). Chung PHAI goi chung ham nay.
 *
 * Day khong phai so thich ve kien truc. Truoc khi co no, man hinh ve ghi chu
 * thanh o vuong 18 diem anh con duong ghi dung mot hop 160 diem da do tu noi
 * dung: cung mot ghi chu, hai hinh dang khac han nhau, va nguoi dung chi phat
 * hien ra sau khi xuat tep. Do dung la kieu "ban xem truoc noi doi" ma bai hoc
 * so 3 noi toi.
 *
 * `widthPt` uu tien be rong nguoi dung da keo; chi khi chua co moi dung mac dinh.
 * CHIEU CAO thi KHONG BAO GIO lay tu doi tuong - luon do ra tu noi dung, vi day
 * la thu duy nhat biet dau tieng Viet chong hai tang can bao nhieu cho.
 */
export function layoutTextAnnotation(
  source: FontSource,
  annotation: {
    rect: PdfRect;
    contents?: string;
    fontSizePt?: number;
    textAlign?: TextAlign;
    bold?: boolean | undefined;
    italic?: boolean | undefined;
  },
): { rect: PdfRect; metrics: TextBoxMetrics } {
  const metrics = measureTextBox(source, annotation.contents ?? '', {
    fontSizePt: annotation.fontSizePt ?? DEFAULT_TEXT_SIZE_PT,
    align: annotation.textAlign ?? 'left',
    widthPt: annotation.rect.width > 1 ? annotation.rect.width : DEFAULT_TEXT_BOX_WIDTH_PT,
    // Dam / nghieng di CHUNG duong voi co chu va can le: cung mot phep tinh cho
    // ca man hinh lan tep, khong co nhanh rieng nao.
    bold: annotation.bold,
    italic: annotation.italic,
  });
  const anchor = { x: annotation.rect.x, y: annotation.rect.y + annotation.rect.height };
  return { rect: textBoxRect(anchor, metrics), metrics };
}
